// src/pages/ApostilaPreviewPage.tsx
// Wrapper fino da rota pública /previa/:urlSlug — resolve a validade do link
// de prévia (existe? expirou?) e, uma vez válido, entrega os dados prontos
// pro MESMO componente de leitura real (ResumoReadPage, via prop
// previewData), em vez de reimplementar a experiência de leitura aqui.
// Isso é o que elimina a divergência de CSS/tipografia/quiz/modo-interagir
// que existia entre a prévia e a leitura real antes desta unificação.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apostilaPreviewService, { ApostilaPreviewData } from '../services/apostilaPreview.service';
import { ResumoPage } from '../components/resumo/components/PageNavigator';
import ResumoReadPage from './ResumoReadPage';
import { useTheme } from '../contexts/ThemeContext';
import { BookOpen } from 'lucide-react';
import './ApostilaPreviewPage.css';

const ApostilaPreviewPage: React.FC = () => {
    const { urlSlug } = useParams<{ urlSlug: string }>();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const darkMode = theme === 'dark';

    const [data, setData] = useState<ApostilaPreviewData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expired, setExpired] = useState(false);

    useEffect(() => {
        if (!urlSlug) {
            setError('Link inválido.');
            setLoading(false);
            return;
        }
        apostilaPreviewService.readBySlug(urlSlug)
            .then((res) => {
                // Mesma normalização de sempre — o backend não garante ordem.
                const sortedPages = [...(res.pages || [])]
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) as ResumoPage[];
                setData({ ...res, pages: sortedPages });
                setLoading(false);
            })
            .catch((err) => {
                const isExpired = err?.response?.data?.expired === true;
                const msg = err?.response?.data?.message || 'Link inválido ou expirado.';
                setError(msg);
                setExpired(isExpired);
                setLoading(false);
            });
    }, [urlSlug]);

    // Verifica expiração continuamente (sem countdown) — se expirar enquanto
    // a pessoa está lendo, troca pra tela de erro (desmonta o ResumoReadPage).
    useEffect(() => {
        if (!data?.expiresAt) return;
        const check = () => {
            if (new Date() > new Date(data.expiresAt)) setExpired(true);
        };
        check();
        const interval = setInterval(check, 10000);
        return () => clearInterval(interval);
    }, [data?.expiresAt]);

    if (loading) {
        return (
            <div className={`app-page ${darkMode ? 'dark' : ''}`}>
                <div className="app-loading">
                    <BookOpen size={40} strokeWidth={1.2} />
                    <p>Carregando prévia...</p>
                </div>
            </div>
        );
    }

    if (error || expired || !data) {
        return (
            <div className={`app-page app-page-error ${darkMode ? 'dark' : ''}`}>
                <div className="app-error-card">
                    <BookOpen size={52} strokeWidth={1.2} className="app-error-icon" />
                    <h2>Prévia indisponível</h2>
                    <p>{error || 'Este link de prévia expirou.'}</p>
                    <p className="app-error-contact">
                        Entre em contato com <strong>GRAIA</strong> para ter acesso a prévias.
                    </p>
                    <button className="app-btn-primary" onClick={() => navigate('/login')}>
                        Acessar a plataforma
                    </button>
                </div>
            </div>
        );
    }

    return <ResumoReadPage previewData={data} />;
};

export default ApostilaPreviewPage;
